import React from "react";
import Layout from "../../components/Layout.tsx";
import Title from "../../components/Title.tsx";
import {
  Panel,
  PanelBody,
  PanelHeader,
} from "../../components/panel/panel.jsx";
import { useNavigate } from "react-router-dom";
import ModalContainer from "../../components/ModalContainer.tsx";
import TemplateForm from "../../components/perception/TemplateForm.tsx";
import {
  findPerceptionTemplates,
  deletePerceptionTemplate,
  PerceptionTemplate,
} from "../../services/perceptions.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import { useAppSettings } from "../../hooks/useAppSettings.tsx";

const BonusTemplateListPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = React.useState<PerceptionTemplate[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);
  const [editingTemplate, setEditingTemplate] =
    React.useState<PerceptionTemplate | null>(null);
  const [sortBy, setSortBy] = React.useState<string>("");
  const [defaultSort, setDefaultSort] = React.useState<"asc" | "desc">("asc");
  useAppSettings();
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await findPerceptionTemplates();
      setTemplates(data || []);
    } catch (error) {
      console.error("Error loading templates:", error);
      notification("Error ⚠️", "Error al cargar las plantillas", "danger");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loadTemplates();
  }, []);

  const handleOpenModal = () => {
    setEditingTemplate(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTemplate(null);
  };

  const handleEdit = (template: PerceptionTemplate) => {
    setEditingTemplate(template);
    setShowModal(true);
  };

  const handleDelete = async (template: PerceptionTemplate) => {
    if (
      window.confirm(
        `¿Estás seguro de eliminar la plantilla "${template.name}"?`
      )
    ) {
      try {
        await deletePerceptionTemplate(template.id);
        notification("", "Plantilla eliminada exitosamente", "success");
        loadTemplates();
      } catch (error) {
        console.error("Error deleting template:", error);
        notification("Error ⚠️", "Error al eliminar la plantilla", "danger");
      }
    }
  };

  const handleSuccess = () => {
    loadTemplates();
    handleCloseModal();
  };

  const getIconSort = () => {
    return (
      <i
        className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`}
      />
    );
  };

  const setSort = (sort: string) => {
    const sortOrder =
      sortBy !== sort ? "asc" : defaultSort === "asc" ? "desc" : "asc";

    setSortBy(sort);
    setDefaultSort(sortOrder);
  };

  const sortData = (data: PerceptionTemplate[]) => {
    if (!sortBy) return data;

    return [...data].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case "name":
          aValue = (a.name || "").toLowerCase();
          bValue = (b.name || "").toLowerCase();
          break;
        case "description":
          aValue = (a.description || "").toLowerCase();
          bValue = (b.description || "").toLowerCase();
          break;
        case "amount":
          aValue = Number(a.amount) || 0;
          bValue = Number(b.amount) || 0;
          break;
        case "createdAt":
          aValue = new Date(a.createdAt || "").getTime();
          bValue = new Date(b.createdAt || "").getTime();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return defaultSort === "asc" ? -1 : 1;
      if (aValue > bValue) return defaultSort === "asc" ? 1 : -1;
      return 0;
    });
  };

  const sortedTemplates = React.useMemo(
    () => sortData(templates),
    [templates, sortBy, defaultSort]
  );

  const formatMoney = (amount: number) => {
    return amount.toLocaleString("es-MX", {
      style: "currency",
      currency: "MXN",
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (date: string) => {
    if (!date || date === "-" || date === "" || date === "Invalid Date")
      return "-";

    try {
      if (date.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [year, month, day] = date.split("-");
        return `${day}/${month}/${year}`;
      }

      const dateObj = new Date(date + "T00:00:00");
      if (isNaN(dateObj.getTime())) return "-";

      return dateObj.toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "-";
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <Layout loading={loading}>
      <Title
        baseTitle="Nómina"
        basePath="/nomina"
        activeTitle="Plantillas de Bonos"
        title="Plantillas de Bonos"
      />

      <div className="card border-0 m-4 rounded">
        <Panel>
          <PanelHeader noButton={true} className="bg-azul">
            <div className="d-flex justify-content-between align-items-center w-100">
              <span>Listado de plantillas de bonos</span>
              <button
                className="btn btn-warning btn-sm"
                onClick={handleOpenModal}
              >
                <i className="fa fa-plus me-2"></i>
                Nueva Plantilla
              </button>
            </div>
          </PanelHeader>
          <PanelBody>
            <div className="p-3">
              <div className="table-responsive">
                <table className="table table-striped table-hover table-panel text-nowrap align-middle mb-0">
                  <thead>
                    <tr
                      style={{
                        position: "sticky",
                        top: 0,
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          width: "30%",
                        }}
                        onClick={() => setSort("name")}
                      >
                        Nombre {sortBy === "name" && getIconSort()}
                      </th>
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          width: "25%",
                          maxWidth: "200px",
                        }}
                        onClick={() => setSort("description")}
                      >
                        Descripción {sortBy === "description" && getIconSort()}
                      </th>
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          width: "15%",
                        }}
                        onClick={() => setSort("amount")}
                      >
                        Monto {sortBy === "amount" && getIconSort()}
                      </th>
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          width: "15%",
                        }}
                        onClick={() => setSort("createdAt")}
                      >
                        Fecha {sortBy === "createdAt" && getIconSort()}
                      </th>
                      <th
                        className="text-uppercase text-muted small py-3 px-3"
                        style={{
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          width: "15%",
                        }}
                      >
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedTemplates.map((template, idx) => (
                      <tr
                        key={template.id || idx}
                        style={{ borderBottom: "1px solid #f1f3f4" }}
                      >
                        <td
                          className="py-3 px-3 fw-medium"
                          style={{ width: "30%" }}
                        >
                          {template.name}
                        </td>
                        <td
                          className="py-3 px-3"
                          style={{
                            width: "25%",
                            maxWidth: "200px",
                            wordWrap: "break-word",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                          title={template.description}
                        >
                          {truncateText(template.description, 100)}
                        </td>
                        <td
                          className="py-3 px-3 fw-medium"
                          style={{ width: "15%" }}
                        >
                          {formatMoney(template.amount)}
                        </td>
                        <td className="py-3 px-3" style={{ width: "15%" }}>
                          {formatDate(template.createdAt)}
                        </td>
                        <td className="py-3 px-3" style={{ width: "15%" }}>
                          <button
                            className="btn btn-sm btn-info me-1"
                            onClick={() => handleEdit(template)}
                            title="Editar plantilla"
                          >
                            <i className="fa fa-edit"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(template)}
                            title="Eliminar plantilla"
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {templates.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-5">
                          <div className="d-flex align-items-center justify-content-center">
                            <div className="d-flex flex-column align-items-center justify-content-center">
                              <i className="fa fa-bookmark fa-3x text-muted mb-3"></i>
                              <h5 className="text-secondary text-center fs-20px">
                                No existen plantillas de bonos
                              </h5>
                              <p className="text-muted">
                                Crea tu primera plantilla para agilizar el
                                proceso
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </PanelBody>
        </Panel>
      </div>

      <ModalContainer
        open={showModal}
        closeFn={handleCloseModal}
        title={editingTemplate ? "Editar plantilla" : "Nueva plantilla"}
        size="md"
      >
        <TemplateForm
          close={handleCloseModal}
          onSuccess={handleSuccess}
          templateToEdit={editingTemplate}
          isEditing={!!editingTemplate}
        />
      </ModalContainer>
    </Layout>
  );
};

export default BonusTemplateListPage;
